import React from "react";
import cx from "classnames";
import * as TooltipPrimitives from "@radix-ui/react-tooltip";
import { InformationCircleIcon } from "@heroicons/react/24/outline";
import { FREE_TIER_COLOR, FREE_TIER_NOTE } from "utils/freeTierUtils";

// Renders a price value as strikethrough + "Free" (for use inside a larger price block).
export const FreeTierStrikethrough: React.FC<{ value: string }> = ({
  value,
}) => (
  <span>
    <span className="line-through opacity-50">{value}</span>{" "}
    <span className={FREE_TIER_COLOR}>Free</span>
  </span>
);

// Shared component for displaying a standalone free-tier price (strikethrough + Free badge).
export const FreeTierPrice: React.FC<{ price: string }> = ({ price }) => (
  <span className="flex items-center gap-2">
    <span className="line-through opacity-50">${price}/hr</span>
    <span className={FREE_TIER_COLOR}>Free</span>
  </span>
);

// Renders FreeTierPrice as a tooltip trigger — stops click propagation for use inside clickable cards.
export const FreeTierPriceWithTooltip: React.FC<{ price: string }> = ({
  price,
}) => (
  <TooltipPrimitives.Provider>
    <TooltipPrimitives.Root delayDuration={0}>
      <TooltipPrimitives.Trigger asChild>
        <span onClick={(e) => e.stopPropagation()}>
          <FreeTierPrice price={price} />
        </span>
      </TooltipPrimitives.Trigger>
      <TooltipPrimitives.Portal>
        <TooltipPrimitives.Content
          side="bottom"
          className="z-50 max-w-xs rounded-md bg-black px-3 py-2 text-xs text-white shadow-md"
        >
          {FREE_TIER_NOTE}
          <TooltipPrimitives.Arrow className="fill-black" />
        </TooltipPrimitives.Content>
      </TooltipPrimitives.Portal>
    </TooltipPrimitives.Root>
  </TooltipPrimitives.Provider>
);

// Info icon that shows FREE_TIER_NOTE in a tooltip — for use in headers/cost summaries.
export const FreeTierInfoTooltip: React.FC = () => (
  <TooltipPrimitives.Provider>
    <TooltipPrimitives.Root delayDuration={300}>
      <TooltipPrimitives.Trigger asChild>
        <span>
          <InformationCircleIcon className="h-4 w-4 text-secondary flex-shrink-0" />
        </span>
      </TooltipPrimitives.Trigger>
      <TooltipPrimitives.Portal>
        <TooltipPrimitives.Content
          side="top"
          sideOffset={4}
          className="z-50 max-w-[220px] rounded-md bg-zinc-800 px-3 py-2 text-sm text-zinc-300 shadow-md leading-relaxed"
        >
          {FREE_TIER_NOTE}
          <TooltipPrimitives.Arrow className="fill-zinc-800" />
        </TooltipPrimitives.Content>
      </TooltipPrimitives.Portal>
    </TooltipPrimitives.Root>
  </TooltipPrimitives.Provider>
);

// Renders the compute / storage / total pricing rows for the running rate.
// Used inside pricing tooltips across the instance selection and deploy UI,
// and on environment cards (where `liveState` drives per-row status dots).
export type ChargeState = "billing" | "idle" | "free";
export type StorageChargeState =
  | ChargeState
  | "included-billing"
  | "included-idle";

export interface PricingLiveState {
  compute: ChargeState;
  storage: StorageChargeState;
}

interface PricingRateRowsProps {
  computePrice: number;
  storageCost: number;
  /** 0 or falsy = storage is included at no extra charge (shows "Included") */
  pricePerGbHr: number;
  isFreeTierCompute: boolean;
  isFreeTierStorage: boolean;
  /**
   * When passed, rows render a coloured dot and amounts reflect the instance's
   * current charge state (e.g. compute shows $0.00/hr when stopped). The Total
   * row sums only currently-billing components. Used on environment cards.
   * When omitted, the rows render the running rate (Create / Deploy pages).
   */
  liveState?: PricingLiveState;
}

const StatusDot: React.FC<{ tone: "billing" | "neutral" }> = ({ tone }) => (
  <span
    className={cx(
      "mr-2 inline-block size-[0.55rem] shrink-0 rounded-full",
      tone === "billing" &&
        "bg-highlight shadow-[0_0_8px_rgba(118,185,0,0.55)]",
      tone === "neutral" && "bg-zinc-600",
    )}
    aria-hidden="true"
  />
);

// Free and displayed-zero values are neutral because no money leaves the
// wallet. Nonzero active charges use the product highlight color.
const displaysAsZero = (value: number) => Number(value.toFixed(2)) === 0;

const computeDotTone = (
  state: ChargeState,
  computePrice: number,
): "billing" | "neutral" =>
  state === "billing" && !displaysAsZero(computePrice) ? "billing" : "neutral";

const storageDotTone = (
  state: StorageChargeState,
  storageCost: number,
): "billing" | "neutral" =>
  (state === "billing" || state === "included-billing") &&
  !displaysAsZero(storageCost)
    ? "billing"
    : "neutral";

const liveRate = (price: number, inactive: boolean) => {
  const value = `$${price.toFixed(2)}/hr`;
  return inactive ? (
    <span className="line-through opacity-50">{value}</span>
  ) : (
    value
  );
};

const liveStorageRate = (
  state: StorageChargeState | undefined,
  price: number,
) => {
  if (state === "included-billing") {
    return <span className="text-zinc-400">Included</span>;
  }
  if (state === "included-idle") {
    return <span className="text-zinc-400">Not charging</span>;
  }
  return liveRate(price, state === "idle" || state === "free");
};

export const PricingRateRows: React.FC<PricingRateRowsProps> = ({
  computePrice,
  storageCost,
  pricePerGbHr,
  isFreeTierCompute,
  isFreeTierStorage,
  liveState,
}) => {
  // Default (running-rate) values used by Create / Deploy pages.
  const computeValueDefault = isFreeTierCompute ? (
    <span className="line-through opacity-50">
      ${computePrice.toFixed(2)}/hr
    </span>
  ) : (
    `$${computePrice.toFixed(2)}/hr`
  );

  const storageValueDefault = !pricePerGbHr ? (
    <span className="text-zinc-400">Included</span>
  ) : isFreeTierStorage ? (
    <span className="line-through opacity-50">
      ${storageCost.toFixed(2)}/hr
    </span>
  ) : (
    `$${storageCost.toFixed(2)}/hr`
  );

  // Live (env-card) per-row rendering.
  const computeValueLive = liveRate(
    computePrice,
    liveState?.compute === "idle" || liveState?.compute === "free",
  );
  const storageValueLive = liveStorageRate(liveState?.storage, storageCost);

  // Total: free-tier UI is unchanged from the running-rate view; live mode
  // sums only components that are actively billing so users see real burn.
  const totalValue = (() => {
    if (liveState) {
      const computeBilling = liveState.compute === "billing" ? computePrice : 0;
      const storageBilling = liveState.storage === "billing" ? storageCost : 0;
      const liveTotal =
        parseFloat(computeBilling.toFixed(2)) +
        parseFloat(storageBilling.toFixed(2));
      return `$${liveTotal.toFixed(2)}/hr`;
    }
    const runningTotal =
      parseFloat(computePrice.toFixed(2)) + parseFloat(storageCost.toFixed(2));
    if (isFreeTierCompute && isFreeTierStorage)
      return <FreeTierStrikethrough value={`$${runningTotal.toFixed(2)}/hr`} />;
    if (isFreeTierCompute)
      return (
        <span>
          <span className="line-through opacity-50">
            ${runningTotal.toFixed(2)}/hr
          </span>{" "}
          ${storageCost.toFixed(2)}/hr
        </span>
      );
    return `$${runningTotal.toFixed(2)}/hr`;
  })();

  const priceRow = (
    label: string,
    value: React.ReactNode,
    dotTone: "billing" | "neutral" | undefined,
    isTotal = false,
  ) => (
    <div
      className={cx(
        "flex items-baseline gap-1",
        isTotal && "pt-2 mt-1 border-t border-zinc-700",
      )}
    >
      <span
        className={cx(
          "text-sm shrink-0 flex items-center",
          isTotal ? "text-zinc-300 font-medium" : "text-zinc-400",
        )}
      >
        {dotTone && <StatusDot tone={dotTone} />}
        {label}
      </span>
      <span className="flex-1 min-w-[10px] border-b border-dotted border-zinc-600 mb-[3px]" />
      <span
        className={cx(
          "text-sm shrink-0 whitespace-nowrap",
          isTotal ? "text-white font-semibold" : "text-zinc-300",
        )}
      >
        {value}
      </span>
    </div>
  );

  return (
    <div className="flex flex-col gap-1">
      {priceRow(
        "Compute",
        liveState ? computeValueLive : computeValueDefault,
        liveState ? computeDotTone(liveState.compute, computePrice) : undefined,
      )}
      {priceRow(
        "Storage",
        liveState ? storageValueLive : storageValueDefault,
        liveState ? storageDotTone(liveState.storage, storageCost) : undefined,
      )}
      {priceRow("Total", totalValue, undefined, true)}
    </div>
  );
};
